/**
 * Wire types and constants of Editor Activity Tracking (client side).
 *
 * Mirrors `infrastructure/lambda/shared/activity.ts` and
 * `infrastructure/lambda/activity-events/contract.ts` — keep in step (the frontend
 * cannot import infrastructure code; `cognito-proxy/contract.ts` is the same
 * arrangement). Design: `.plans/activity-tracking/`.
 */

export const ACTIVITY_SCHEMA = 1

export const ACTIVITY_SURFACES = ['app', 'task_list', 'segment_editing', 'quick_edit', 'markup', 'group_review'] as const
export type ActivitySurface = (typeof ACTIVITY_SURFACES)[number]

/** The surfaces whose ticks are Editor work on a map (mirrors `ACTIVITY_EDITING_SURFACES` in `lambda/shared/activity.ts`). */
export const ACTIVITY_EDITING_SURFACES = ['segment_editing', 'quick_edit', 'markup'] as const
export type ActivityEditingSurface = (typeof ACTIVITY_EDITING_SURFACES)[number]

export const ACTIVITY_VIAS = ['click', 'menu', 'key', 'map', 'list', 'auto'] as const
export type ActivityVia = (typeof ACTIVITY_VIAS)[number]

export const ACTIVITY_TASK_TYPES = ['segment_editing', 'geometry_markup', 'quick_editing'] as const
export type ActivityTaskType = (typeof ACTIVITY_TASK_TYPES)[number]

export const ACTIVITY_MAX_PAST_DAYS = 14
export const ACTIVITY_TICK_MS = 15_000

export const ACTIVITY_LIMITS = {
  maxBodyBytes: 256 * 1024,
  maxEvents: 500,
  maxTicks: 120,
  maxPropsBytes: 1024,
  maxPropsKeys: 16,
  maxPropString: 64,
  maxPropArray: 16,
} as const

/** Modal names accepted by `modal.open` / `modal.close` (`activityName`). */
export const ACTIVITY_MODAL_NAMES = [
  'tutorial', 'group_tutorial', 'shortcuts', 'palette', 'finalize_confirm', 'unanswered_confirm',
  'finalize_error', 'load_error', 'auto_save_error', 'validation_errors', 'merge_conflict', 'merge_error',
  'draw_in_progress', 'list_inspections', 'list_markups', 'list_new_segments', 'task_filter', 'start_error',
  'whats_new', 'update', 'basemap_key', 'unapplied_changes', 'other',
] as const
export type ActivityModalName = (typeof ACTIVITY_MODAL_NAMES)[number]

// ── Client constants ──────────────────────────────────────────────────────────

export const ACTIVITY_FLUSH_INTERVAL_MS = 30_000
export const ACTIVITY_FLUSH_MAX_EVENTS = 300
export const ACTIVITY_FLUSH_MAX_BYTES = 48 * 1024
export const ACTIVITY_KEEPALIVE_MAX_BYTES = 60 * 1024
export const ACTIVITY_BUFFER_MAX_EVENTS = 2_000
export const ACTIVITY_SESSION_DRIFT_MS = 60_000
export const ACTIVITY_SESSION_HIDDEN_GAP_MS = 5 * 60_000
export const ACTIVITY_LATE_TICK_MS = 30_000
export const ACTIVITY_SLOW_WAIT_MS = 1_000
export const ACTIVITY_WHEEL_GESTURE_GAP_MS = 250
export const ACTIVITY_FORM_FIELD_IDLE_MS = 2_000
export const ACTIVITY_FORM_FIELD_RATE_MS = 10_000
export const ACTIVITY_OUTBOX_MAX_ENTRIES = 500
export const ACTIVITY_500_MAX_ATTEMPTS = 10
export const ACTIVITY_SIGN_OUT_DRAIN_MS = 3_000
/** A drain waits this long (a timer) for an in-flight unload keepalive's outcome. */
export const ACTIVITY_KEEPALIVE_WAIT_MS = 3_000
export const ACTIVITY_MAX_ENTRY_EVENTS = 300
export const ACTIVITY_MAX_ENTRY_TICKS = 120
export const ACTIVITY_JOURNAL_PREFIX = 'rse.activity.journal.'
export const ACTIVITY_JOURNAL_MAX_BYTES = 1024 * 1024

// ── Wire items ────────────────────────────────────────────────────────────────

export type ActivityPropValue =
  | string | number | boolean | null
  | readonly (string | number)[]
  | { readonly [k: string]: string | number | boolean | null }
export type ActivityProps = { readonly [k: string]: ActivityPropValue | undefined }

export interface ActivityEventItem {
  eventId: string
  seq: number
  t: number
  name: string
  surface: ActivitySurface
  taskId: string | null
  taskType: ActivityTaskType | null
  via: ActivityVia | null
  key: string | null
  props: ActivityProps
}

export interface ActivityTickItem {
  t: number
  windowMs: number
  surface: ActivitySurface
  taskId: string | null
  visible: boolean
  focused: boolean
  msSinceInput: number
  wheelZooms: number
  dragPans: number
  keyPans: number
  spacePans: number
  mapClicks: number
  toolbarClicks: number
  panelClicks: number
  shortcutKeys: number
  typingKeys: number
  pointerPx: number
  requests: number
  waitMs: number
  longTaskMs: number
}

export interface ActivityResponse {
  accepted?: { events: number; ticks: number }
  duplicates?: { events: number; ticks: number }
  rejected?: { events: number; ticks: number }
  rejections?: { kind: 'event' | 'tick'; index: number; reason: string }[]
}

/** One persisted unit of sending; items are serialised JSON strings, immutable forever. */
export interface OutboxEntry {
  id: string
  /** `UserService.currentUser.id` (= Cognito sub) that recorded it. */
  userId: string
  sessionId: string
  createdAtWall: number
  /** Minimum `t` of its items (14-day expiry). */
  oldestT: number
  events: string[]
  ticks: string[]
  /** Sum of the items' lengths. */
  bytes: number
  attempts: number
  nextAttemptAt: number
}

export interface ActivityViewport { w: number; h: number; dpr: number }
